import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ListService } from '../../../../service/data/list.service';

@Injectable({
  providedIn: 'root',
})
export class VehicleDataService {
  // Almacenamiento en memoria para marcas
  private vehicleBrandsSubject = new BehaviorSubject<any[]>([]);
  private vehicleBrandsFormatted = new BehaviorSubject<
    { value: string; label: string }[]
  >([]);
  private brandsLoaded = false;

  // Almacenamiento en memoria para tipos
  private vehicleTypesSubject = new BehaviorSubject<any[]>([]);
  private vehicleTypesFormatted = new BehaviorSubject<
    { value: string; label: string }[]
  >([]);
  private typesLoaded = false;

  // Almacenamiento en memoria para años
  private modelYearsFormatted = new BehaviorSubject<
    { value: string; label: string }[]
  >([]);
  private yearsLoaded = false;

  // Almacenamiento en memoria para tipos de combustible
  private fuelTypesFormatted = new BehaviorSubject<
    { value: string; label: string }[]
  >([]);
  private fuelTypesLoaded = false;

  // Estado de colores
  private colorsFormatted = new BehaviorSubject<any[]>([]);
  private colorsLoaded = false;

  constructor(private listService: ListService) {}

  /**
   * Obtener marcas de vehículos (carga solo una vez por sesión)
   */
  getVehicleBrands(): Observable<{ value: string; label: string }[]> {
    if (!this.brandsLoaded) {
      this.loadVehicleBrands();
    }
    return this.vehicleBrandsFormatted.asObservable();
  }

  getColors(): Observable<{ value: string; label: string }[]> {
    if (!this.colorsLoaded) {
      this.loadColors();
    }
    return this.colorsFormatted.asObservable();
  }

  /**
   * Obtener tipos de vehículos (carga solo una vez por sesión)
   */
  getVehicleTypes(): Observable<{ value: string; label: string }[]> {
    if (!this.typesLoaded) {
      this.loadVehicleTypes();
    }
    return this.vehicleTypesFormatted.asObservable();
  }

  /**
   * Obtener años de modelo (genera solo una vez por sesión)
   */
  getModelYears(): Observable<{ value: string; label: string }[]> {
    if (!this.yearsLoaded) {
      this.generateModelYears();
    }
    return this.modelYearsFormatted.asObservable();
  }

  /**
   * Obtener tipos de combustible (genera solo una vez por sesión)
   */
  getFuelTypes(): Observable<{ value: string; label: string }[]> {
    if (!this.fuelTypesLoaded) {
      this.loadFuelTypes();
    }
    return this.fuelTypesFormatted.asObservable();
  }

  /**
   * Cargar marcas de vehículos desde la API
   */
  private loadVehicleBrands(): void {
    this.listService
      .getlistVehicleBrand()
      .pipe(
        tap((response: any) => {
          if (
            response &&
            response.vehiclebrands &&
            Array.isArray(response.vehiclebrands)
          ) {
            const brands = response.vehiclebrands;
            this.vehicleBrandsSubject.next(brands);

            const formatted = brands.map((brand: any) => ({
              value: brand.name,
              label: brand.name,
            }));

            this.vehicleBrandsFormatted.next(formatted);
            this.brandsLoaded = true;
          }
        }),
        catchError((error) => {
          return of([]);
        })
      )
      .subscribe();
  }

  /**
   * Cargar tipos de vehículos desde la API
   */
  private loadVehicleTypes(): void {
    this.listService
      .getlistVehicleType()
      .pipe(
        tap((response: any) => {
          if (
            response &&
            response.vehicletypes &&
            Array.isArray(response.vehicletypes)
          ) {
            const types = response.vehicletypes;
            this.vehicleTypesSubject.next(types);

            const formatted = types.map((type: any) => ({
              value: type.type,
              label: type.name,
            }));

            this.vehicleTypesFormatted.next(formatted);
            this.typesLoaded = true;
          }
        }),
        catchError((error) => {
          return of([]);
        })
      )
      .subscribe();
  }

  private loadColors(): void {
    this.listService
      .getColorList()
      .pipe(
        tap((response: any) => {
          if (
            response &&
            response.vehicles &&
            Array.isArray(response.vehicles)
          ) {
            this.colorsFormatted.next(response.vehicles);
            this.colorsLoaded = true;
          }
        }),
        catchError((error) => {
          return of([]);
        })
      )
      .subscribe();
  }

  private loadFuelTypes(): void {
    this.listService
      .getFuelTypeList()
      .pipe(
        tap((response: any) => {
          if (response && Array.isArray(response)) {
            const formatted = response.map((fuel: any) => ({
              value: fuel.id.toString(),
              label: fuel.name,
            }));

            this.fuelTypesFormatted.next(formatted);
            this.fuelTypesLoaded = true;
          }
        }),
        catchError((error) => {
          return of([]);
        })
      )
      .subscribe();
  }

  /**
   * Generar lista de años para el modelo desde 1990 hasta el año actual + 1
   */
  private generateModelYears(): void {
    const currentYear = new Date().getFullYear();
    const years = [];

    for (let year = currentYear + 1; year >= 1900; year--) {
      const yearStr = year.toString();
      years.push({ value: yearStr, label: yearStr });
    }

    this.modelYearsFormatted.next(years);
    this.yearsLoaded = true;
  }

  /**
   * Limpiar todos los datos en caché (útil para cerrar sesión)
   */
  clearCache(): void {
    this.vehicleBrandsSubject.next([]);
    this.vehicleBrandsFormatted.next([]);
    this.vehicleTypesSubject.next([]);
    this.vehicleTypesFormatted.next([]);
    this.modelYearsFormatted.next([]);
    this.fuelTypesFormatted.next([]);

    this.brandsLoaded = false;
    this.typesLoaded = false;
    this.yearsLoaded = false;
    this.fuelTypesLoaded = false;
  }
}
